import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/admin/date-field";
import { ReferenceField } from "@/components/admin/reference-field";

import type { CrmDataProvider } from "../providers/types";
import { validateFiscalDocumentFile } from "../providers/commons/developerFiscalDocuments";
import type { DeveloperFiscalDocument, Sale } from "../types";

/**
 * Opens a blank tab synchronously with the click, then navigates it once the
 * signed URL resolves. Doing this after the `await` (i.e. calling
 * `window.open` with the final URL) gets silently blocked by popup blockers
 * in every major browser, since it is no longer perceived as a direct
 * response to the user gesture.
 */
async function openSignedDocument(
  fetchUrl: () => Promise<string>,
): Promise<void> {
  const tab = window.open("", "_blank");
  const url = await fetchUrl();
  if (tab) {
    tab.location.href = url;
  }
}

/**
 * "Constancia de Situacion Fiscal" upload control and version list (Almikia
 * Epic E1b, TASK-035). Mounted by `DeveloperFiscalSection`, which is only
 * ever rendered inside the `developer_fiscal_profiles` `CanAccess` gate on
 * `DeveloperShow` (direccion/contabilidad only) -- mirrored at the database
 * RLS layer by `can_access_fiscal_data()` (TASK-027), so this component never
 * fetches or renders for another role.
 *
 * ASSUMPTION: no delete or soft-delete action ships in this epic -- the
 * document lifecycle (who can retire a version, and how) arrives with E5.
 * Every upload adds a new version; nothing is ever replaced.
 */
export const DeveloperFiscalDocuments = ({
  developerId,
}: {
  developerId: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: versions, refetch } = useGetList<DeveloperFiscalDocument>(
    "developer_fiscal_documents",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "uploaded_at", order: "DESC" },
      filter: { developer_id: developerId, "deleted_at@is": null },
    },
  );

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationKey: ["developer-fiscal-document-upload", developerId],
    mutationFn: (file: File) =>
      dataProvider.uploadDeveloperFiscalDocument(developerId, file),
    onSuccess: () => {
      refetch();
      notify("resources.developers.fiscal_documents.upload_success", {
        messageArgs: { _: "Constancia uploaded" },
      });
    },
    onError: () => {
      notify("resources.developers.fiscal_documents.upload_error", {
        type: "error",
        messageArgs: { _: "The Constancia could not be uploaded" },
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset now so re-selecting the same rejected file still fires onChange.
    event.target.value = "";
    if (!file) return;

    const validation = validateFiscalDocumentFile(file);
    if (!validation.valid) {
      notify(
        validation.reason === "invalid_type"
          ? "resources.developers.fiscal_documents.error_invalid_type"
          : "resources.developers.fiscal_documents.error_too_large",
        {
          type: "error",
          messageArgs: {
            _:
              validation.reason === "invalid_type"
                ? "Only PDF files are accepted"
                : "The file exceeds the 10 MB limit",
          },
        },
      );
      return;
    }

    upload(file);
  };

  const handleView = (version: DeveloperFiscalDocument) => {
    openSignedDocument(() =>
      dataProvider.getDeveloperFiscalDocumentUrl(version),
    ).catch(() => {
      notify("resources.developers.fiscal_documents.view_error", {
        type: "error",
        messageArgs: { _: "The document could not be opened" },
      });
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium">
        {translate("resources.developers.fiscal_documents.title", {
          _: "Constancia de Situacion Fiscal",
        })}
      </h4>
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={handleFileChange}
        />
        {/* This section's primary action (design-system.md section 6): the
            screen's single gradient action is already "Editar" (DeveloperShow
            header), so uploading a new version uses the solid-navy "default"
            style instead of a second gradient button. */}
        <Button
          type="button"
          variant="default"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {translate("resources.developers.fiscal_documents.upload_button", {
            _: "Upload Constancia",
          })}
        </Button>
      </div>
      {/* No delete or soft-delete action here.
          ASSUMPTION (Almikia Epic E1b): the document lifecycle -- who can
          retire a version, and how -- arrives with E5. */}
      {versions && versions.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {versions.map((version) => (
            <li
              key={version.id}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="flex flex-col">
                <DateField
                  source="uploaded_at"
                  record={version}
                  showTime
                  className="tabular-nums"
                />
                <span className="text-muted-foreground">
                  <ReferenceField<DeveloperFiscalDocument, Sale>
                    source="uploaded_by"
                    reference="sales"
                    record={version}
                    error={translate(
                      "resources.developers.fiscal_documents.uploaded_by_unknown",
                      { _: "Unknown user" },
                    )}
                    render={({ referenceRecord }) =>
                      referenceRecord
                        ? `${referenceRecord.first_name} ${referenceRecord.last_name}`
                        : null
                    }
                  />
                </span>
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleView(version)}
              >
                {translate("resources.developers.fiscal_documents.view", {
                  _: "View",
                })}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("resources.developers.fiscal_documents.empty", {
            _: "No versions uploaded yet",
          })}
        </p>
      )}
    </div>
  );
};
